import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

/**
 * Client-side SVD mechanism, mirroring `should-use-design-system.ts`. Ships TWO independent
 * decisions, both computed server-side from the same `ramp_svd-rollout-web` bucket:
 *  - `shouldUseSvdCard()` — "render the SVD product card on THIS page?" — reflects
 *    `pageProps.isSvdEnabled` (bucket AND per-page surface eligibility, `computeIsSvdActive`).
 *  - `isSvdFlowEnabled()` — "is this user in the SVD flow AT ALL?" — reflects
 *    `pageProps.isSvdFlowEnabled` (bucket alone, `isSvdRolloutBucketActive`), independent of the
 *    current page. Data-model decisions that must stay consistent across every page (e.g. the
 *    wishlist login-merge picking the v2 vs legacy store) gate on this one, not on
 *    `shouldUseSvdCard()` — a user can be in the SVD rollout while browsing a page whose card isn't
 *    SVD-eligible.
 *
 * These functions only REFLECT the page props — they do not own the kill-switch. The single gate is
 * server-side: `getServerSideBaseProps` evaluates the experiment behind `ENABLE_SVD_FLOW_DISCOVERY`
 * (see `svd.config`) and ships both results. `_app` seeds the module state below from those props;
 * `_document` reflects them onto `<html>` so the browser reads the attributes (hydration-safe — never
 * this module state). When the flow is dark the server always sends both flags `false`, so every
 * reader here returns false. Module state is safe because the pages-router render pass is
 * synchronous.
 *
 * These constants live HERE (not `svd.config`) on purpose: this module is on the client render path
 * (every product-card section reads `shouldUseSvdCard()`), so it must stay free of the statsig
 * experiment registry.
 */
export const DOCUMENT_HTML_DATA_SVD_CARD_ATTR = 'data-svd-card'
export const DOCUMENT_HTML_DATA_SVD_CARD_VALUE = 'enabled'

// Marks `<html>` for `ramp_svd-rollout-web` bucket membership (independent of the current page's
// own render decision) — the sitewide "is this user in the SVD flow at all?" fact.
export const DOCUMENT_HTML_DATA_SVD_FLOW_ATTR = 'data-svd-flow'
export const DOCUMENT_HTML_DATA_SVD_FLOW_VALUE = 'enabled'

let svdCardEnabledForRender: boolean | undefined

export function setSvdCardEnabledInMemory(enabled: boolean | undefined | null): void {
  if (isBrowser()) {
    // No DOM mutation during render — the `<html>` attribute is only synced at commit time
    // by syncSvdCardHtmlAttribute() (see should-use-design-system.ts for the rationale).
    // `undefined` = reset to the attribute fallback (tests + the pre-`_app` window); `_app`
    // pins no-decision pages to `false` — the decision is per-page.
    svdCardEnabledForRender = enabled === undefined ? undefined : Boolean(enabled)
    return
  }

  // Server: always reset — fail-closed, never leak the previous request's value.
  svdCardEnabledForRender = Boolean(enabled)
}

export function shouldUseSvdCard(): boolean {
  if (isBrowser()) {
    if (svdCardEnabledForRender !== undefined) {
      return svdCardEnabledForRender
    }
    return document.documentElement.getAttribute(DOCUMENT_HTML_DATA_SVD_CARD_ATTR) === DOCUMENT_HTML_DATA_SVD_CARD_VALUE
  }

  return svdCardEnabledForRender ?? false
}

/**
 * `ramp_svd-rollout-web` bucket membership alone (independent of the current page's own render
 * decision) — the sitewide "is this user in the SVD flow at all?" fact. Seeded by `_app` from
 * `pageProps.isSvdFlowEnabled`; `_app` passes it through unmodified (never pinned to `false` on
 * no-decision pages), the same way `dsRampUserForRender` works in `should-use-design-system.ts` —
 * this is per-USER, not per-page, so it must survive pages that omit the prop.
 *
 * Data-model decisions that must stay consistent regardless of the current page (e.g. the wishlist
 * login-merge picking the v2 vs legacy store) gate on `isSvdFlowEnabled()`, NOT `shouldUseSvdCard()`
 * — a user can be in the SVD rollout while browsing a page that isn't SVD-card-eligible.
 */
let svdFlowEnabledForRender: boolean | undefined

export function setSvdFlowEnabledInMemory(enabled: boolean | undefined | null): void {
  if (isBrowser()) {
    // Same commit-time contract as the card decision above — no DOM mutation during render.
    svdFlowEnabledForRender = enabled === undefined ? undefined : Boolean(enabled)
    return
  }

  // Server: always reset — fail-closed, never leak the previous request's value.
  svdFlowEnabledForRender = Boolean(enabled)
}

export function isSvdFlowEnabled(): boolean {
  if (isBrowser()) {
    if (svdFlowEnabledForRender !== undefined) {
      return svdFlowEnabledForRender
    }
    return document.documentElement.getAttribute(DOCUMENT_HTML_DATA_SVD_FLOW_ATTR) === DOCUMENT_HTML_DATA_SVD_FLOW_VALUE
  }

  return svdFlowEnabledForRender ?? false
}

/**
 * Sole client-side writer of the `data-svd-card` / `data-svd-flow` `<html>` attributes. Called by
 * `_app` from a layout effect (post-commit, pre-paint) so neither attribute flips under a
 * still-visible outgoing page. Each attribute is independently a no-op while its own decision is
 * undecided (`undefined`) — mirrors `syncDesignSystemHtmlAttributes`.
 */
export function syncSvdHtmlAttributes(): void {
  if (!isBrowser()) {
    return
  }

  if (svdCardEnabledForRender !== undefined) {
    if (svdCardEnabledForRender) {
      document.documentElement.setAttribute(DOCUMENT_HTML_DATA_SVD_CARD_ATTR, DOCUMENT_HTML_DATA_SVD_CARD_VALUE)
    } else {
      document.documentElement.removeAttribute(DOCUMENT_HTML_DATA_SVD_CARD_ATTR)
    }
  }

  if (svdFlowEnabledForRender !== undefined) {
    if (svdFlowEnabledForRender) {
      document.documentElement.setAttribute(DOCUMENT_HTML_DATA_SVD_FLOW_ATTR, DOCUMENT_HTML_DATA_SVD_FLOW_VALUE)
    } else {
      document.documentElement.removeAttribute(DOCUMENT_HTML_DATA_SVD_FLOW_ATTR)
    }
  }
}

/** Sole reader used by `_document` to stamp `data-svd-card` on the initial SSR pass. */
export function isSvdCardEnabledFromNextData(nextData: {
  props?: { pageProps?: { isSvdEnabled?: boolean } }
}): boolean {
  return Boolean(nextData.props?.pageProps?.isSvdEnabled)
}

/** Sole reader used by `_document` to stamp `data-svd-flow` on the initial SSR pass. */
export function isSvdFlowEnabledFromNextData(nextData: {
  props?: { pageProps?: { isSvdFlowEnabled?: boolean } }
}): boolean {
  return Boolean(nextData.props?.pageProps?.isSvdFlowEnabled)
}
